import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Save, Trash2, Plus } from 'lucide-react';
import { Stopwatch } from '../components/Stopwatch';
// Game was previously imported from utils/storage, which never exported it.
import { Game, PracticeShot } from '../types/curling';
import { getGame, getPracticeSession, savePracticeSession } from '../utils/storage';

export function PracticeShots() {
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();
  const [shots, setShots] = useState<PracticeShot[]>([]);
  const [currentCurl, setCurrentCurl] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [lsdShotsTeam1, setLsdShotsTeam1] = useState<number[]>([0]);
  const [lsdShotsTeam2, setLsdShotsTeam2] = useState<number[]>([0]);
  const [currentZone, setCurrentZone] = useState<string>('');
  const [game, setGame] = useState<Game | null>(null);
  const [lsdHammerTeam, setLsdHammerTeam] = useState<'team1' | 'team2'>('team1');
  // Gates the save effect. Without it the effect would fire on mount with the
  // initial empty state and overwrite the stored session before it had loaded.
  const [sessionLoaded, setSessionLoaded] = useState(false);

  // Load the stored practice session. The three localStorage keys this page used
  // (practiceShots_/lsdValues_/lsdHammerTeam_) are now one server-side resource.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    getPracticeSession(gameId)
      .then(session => {
        if (cancelled) return;
        setShots(session.shots ?? []);
        setLsdShotsTeam1(session.lsdValuesTeam1?.length ? session.lsdValuesTeam1 : [0]);
        setLsdShotsTeam2(session.lsdValuesTeam2?.length ? session.lsdValuesTeam2 : [0]);
        if (session.lsdHammerTeam) setLsdHammerTeam(session.lsdHammerTeam);
        setSessionLoaded(true);
      })
      .catch(error => {
        // Deliberately leaves sessionLoaded false so a failed read cannot be
        // followed by a write that discards whatever is on the server.
        console.error('Failed to load practice session:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Persist the session whenever any part of it changes.
  useEffect(() => {
    if (!gameId || !sessionLoaded) return;

    savePracticeSession(gameId, {
      shots,
      lsdValuesTeam1: lsdShotsTeam1,
      lsdValuesTeam2: lsdShotsTeam2,
      lsdHammerTeam,
    }).catch(error => console.error('Failed to save practice session:', error));
  }, [gameId, sessionLoaded, shots, lsdShotsTeam1, lsdShotsTeam2, lsdHammerTeam]);

  // Once both teams have thrown at least one real LSD (zero is the unfilled
  // placeholder, not a measurement), default hammer to whichever is closer to
  // the button on average — the actual LSD contest rule — so the selector
  // doesn't sit on team1 by default when the draws say otherwise.
  useEffect(() => {
    const team1Values = lsdShotsTeam1.filter(v => v > 0);
    const team2Values = lsdShotsTeam2.filter(v => v > 0);
    if (team1Values.length === 0 || team2Values.length === 0) return;

    const average = (values: number[]) => values.reduce((sum, v) => sum + v, 0) / values.length;
    setLsdHammerTeam(average(team1Values) <= average(team2Values) ? 'team1' : 'team2');
  }, [lsdShotsTeam1, lsdShotsTeam2]);

  // Load game details
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    getGame(gameId)
      .then(currentGame => {
        if (!cancelled && currentGame) setGame(currentGame);
      })
      .catch(error => console.error('Failed to load game:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const zones = [
    '0. Hogged',
    '1. High Guard',
    '2. Mid Guard',
    '3. Tight Guard',
    '4. Top Twelve',
    '5. Top Eight',
    '6. Top Four',
    '7. Tee Line',
    '8. Back Four',
    '9. Back Eight',
    '10. Back Twelve',
    '11. Hack',
    '12. Board',
    '13. Control',
    '14. Normal',
    '15. Firm',
    '16. Peel'
  ];

  const calculateLSDAverage = (lsdShots: number[]) => {
    if (lsdShots.length === 0) return 0;
    return lsdShots.reduce((sum, val) => sum + val, 0) / lsdShots.length;
  };

  const lsdShotsForTeam = (team: 'team1' | 'team2') =>
    team === 'team1' ? lsdShotsTeam1 : lsdShotsTeam2;
  const setLsdShotsForTeam = (team: 'team1' | 'team2') =>
    team === 'team1' ? setLsdShotsTeam1 : setLsdShotsTeam2;

  const handleAddLSDShot = (team: 'team1' | 'team2') => {
    setLsdShotsForTeam(team)([...lsdShotsForTeam(team), 0]);
  };

  const handleUpdateLSDShot = (team: 'team1' | 'team2', index: number, value: number) => {
    const newShots = [...lsdShotsForTeam(team)];
    newShots[index] = value;
    setLsdShotsForTeam(team)(newShots);
  };

  const handleRemoveLSDShot = (team: 'team1' | 'team2', index: number) => {
    const shots = lsdShotsForTeam(team);
    if (shots.length > 1) {
      setLsdShotsForTeam(team)(shots.filter((_, i) => i !== index));
    }
  };

  const renderLSDSection = (team: 'team1' | 'team2') => {
    const teamShots = lsdShotsForTeam(team);
    const teamName = team === 'team1'
      ? (game?.team1Name || 'Team 1')
      : (game?.team2Name || 'Team 2');
    // Zero is the unfilled placeholder, not a measurement, so it's excluded
    // from the average shown beside the title.
    const enteredShots = teamShots.filter(v => v > 0);
    const teamAverage = enteredShots.length > 0 ? calculateLSDAverage(enteredShots) : null;
    return (
      <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-baseline gap-2">
            <h2 className="text-xl font-bold text-white">{teamName}'s Last Stone Draw(s)</h2>
            {teamAverage !== null && (
              <span className="text-xl font-semibold text-blue-300">Avg: {teamAverage.toFixed(1)} cm</span>
            )}
          </div>
          <button
            onClick={() => handleAddLSDShot(team)}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200 font-semibold"
          >
            <Plus className="w-5 h-5" />
            Add Shot
          </button>
        </div>

        <div className="space-y-3">
          {teamShots.map((shot, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1 space-y-1">
                <label className="block text-sm font-semibold text-blue-200">Shot {index + 1} (cm)</label>
                <input
                  type="number"
                  value={shot || ''}
                  onChange={(e) => handleUpdateLSDShot(team, index, parseFloat(e.target.value) || 0)}
                  step="0.1"
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                  placeholder="0.0"
                />
              </div>
              {teamShots.length > 1 && (
                <button
                  onClick={() => handleRemoveLSDShot(team, index)}
                  className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/20 rounded transition-colors mt-6"
                  title="Remove shot"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleSaveShot = () => {
    const lsdAverage = calculateLSDAverage([...lsdShotsTeam1, ...lsdShotsTeam2]);

    if (currentTime === 0 || currentZone === '' || currentCurl === 0) {
      alert('Please record time, curl, and select a zone before saving.');
      return;
    }

    const newShot: PracticeShot = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      curl: currentCurl,
      time: currentTime,
      lastStoneDraw: lsdAverage,
      zone: currentZone,
    };

    setShots([newShot, ...shots]);

    // Reset current values (but keep LSD values)
    setCurrentCurl(0);
    setCurrentTime(0);
    setCurrentZone('');
  };

  const handleDeleteShot = (id: string) => {
    setShots(shots.filter(shot => shot.id !== id));
  };

  const calculateAverages = () => {
    if (shots.length === 0) return { curl: 0, time: 0 };
    
    const totalCurl = shots.reduce((sum, shot) => sum + shot.curl, 0);
    const totalTime = shots.reduce((sum, shot) => sum + shot.time, 0);
    
    return {
      curl: totalCurl / shots.length,
      time: totalTime / shots.length,
    };
  };

  const averages = calculateAverages();
  const lsdAverage = calculateLSDAverage([...lsdShotsTeam1, ...lsdShotsTeam2]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(`/CurlingCodex/game/${gameId}`)}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Game
          </button>
          <h1 className="text-3xl font-bold text-white">Practice Shots</h1>
          <div className="w-24"></div>
        </div>

        {/* Session Averages */}
        {shots.length > 0 && (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-sm text-blue-300 mb-1">Avg Time</div>
                <div className="text-2xl font-bold text-white">{averages.time.toFixed(2)}s</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-300 mb-1">Avg Curl</div>
                <div className="text-2xl font-bold text-white">{averages.curl.toFixed(1)} ft</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-300 mb-1">Avg Last Stone Draw</div>
                <div className="text-2xl font-bold text-white">{lsdAverage.toFixed(1)} cm</div>
              </div>
            </div>
          </div>
        )}

        {/* Last Stone Draw Sections, one per team */}
        {renderLSDSection('team1')}
        {renderLSDSection('team2')}

        {/* Assign Hammer to Team */}
        {game && (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-3">Assign Hammer to Team:</h2>
            <select
              value={lsdHammerTeam}
              onChange={(e) => setLsdHammerTeam(e.target.value as 'team1' | 'team2')}
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            >
              <option value="team1" className="bg-blue-950">{game.team1Name || 'Team 1'}</option>
              <option value="team2" className="bg-blue-950">{game.team2Name || 'Team 2'}</option>
            </select>
          </div>
        )}

        {/* Current Shot Recording */}
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Record Practice Shot</h2>

          <div className="space-y-6 mb-6">
            {/* Stopwatch */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-blue-200">Time</label>
              <Stopwatch onTimeUpdate={setCurrentTime} value={currentTime} />
            </div>

            {/* Zone Selector */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-blue-200">Zone</label>
              <select
                value={currentZone}
                onChange={(e) => setCurrentZone(e.target.value)}
                className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-400/60"
              >
                <option value="" className="bg-blue-950">Select a zone</option>
                {zones.map(zone => (
                  <option key={zone} value={zone} className="bg-blue-950">{zone}</option>
                ))}
              </select>
            </div>

            {/* Curl field */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-blue-200">Curl (ft)</label>
              <input
                type="number"
                step="0.1"
                value={currentCurl}
                onChange={(e) => setCurrentCurl(parseFloat(e.target.value) || 0)}
                className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                placeholder="Enter curl in feet"
              />
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveShot}
            className="w-full bg-blue-500 hover:bg-blue-400 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Save className="w-5 h-5" />
            Save Practice Shot
          </button>
        </div>

        {/* Shot History */}
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Shot History</h2>

          {shots.length === 0 ? (
            <p className="text-blue-300 text-center py-8">No practice shots recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/15">
                    <th className="text-center py-3 px-4 text-sm font-semibold text-blue-300">Time (s)</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-blue-300">Curl (ft)</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-blue-300">Zone</th>
                  </tr>
                </thead>
                <tbody>
                  {shots.map((shot, idx) => (
                    <tr key={shot.id} className={`border-b border-white/10 hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/5' : ''}`}>
                      <td className="py-3 px-4 text-center font-semibold text-white">
                        {shot.time.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-center font-semibold text-white">
                        {shot.curl.toFixed(1)}
                      </td>
                      <td className="py-3 px-4 text-center font-semibold text-white">
                        {shot.zone}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}